import React from 'react';
import { CommentReaction } from '../../../../types/Comment';
import CustomTooltip from '../../../../components/UI/Tooltip/CustomTooltip';

interface CommentReactionsProps {
  reactions: CommentReaction[];
  currentUserId?: number;
  onToggle: (emoji: string) => void;
}

type GroupedReaction = {
  emoji: string;
  count: number;
  reactedByViewer: boolean;
  names: string[];
};

const groupReactions = (reactions: CommentReaction[], currentUserId?: number): GroupedReaction[] => {
  const byEmoji = new Map<string, GroupedReaction>();
  // Preserve first-seen order so pills don't jump around as reactions arrive.
  for (const r of reactions) {
    const existing = byEmoji.get(r.emoji);
    const name = r.userId === currentUserId ? 'You' : r.User?.fullName || r.User?.email || 'Someone';
    if (existing) {
      existing.count += 1;
      existing.names.push(name);
      if (r.userId === currentUserId) existing.reactedByViewer = true;
    } else {
      byEmoji.set(r.emoji, {
        emoji: r.emoji,
        count: 1,
        reactedByViewer: r.userId === currentUserId,
        names: [name],
      });
    }
  }
  return Array.from(byEmoji.values());
};

const buildTooltip = (group: GroupedReaction): string => {
  // "You", "You and Darko", "You, Darko and 2 others"
  const { names } = group;
  let who: string;
  if (names.length === 1) who = names[0];
  else if (names.length === 2) who = `${names[0]} and ${names[1]}`;
  else who = `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
  return `${who} reacted with ${group.emoji}`;
};

export const CommentReactions: React.FC<CommentReactionsProps> = ({ reactions, currentUserId, onToggle }) => {
  if (!reactions?.length) return null;

  const groups = groupReactions(reactions, currentUserId);

  return (
    <div className="flex flex-wrap items-center gap-1 mt-2">
      {groups.map((group) => (
        <CustomTooltip key={group.emoji} title={buildTooltip(group)}>
          <div
            role="button"
            tabIndex={0}
            onClick={() => onToggle(group.emoji)}
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-sm leading-5 cursor-pointer transition-colors ${
              group.reactedByViewer
                ? 'bg-blue-200 text-blue-700'
                : 'bg-neutral-200 text-neutral-900 hover:bg-neutral-300'
            }`}
          >
            <span className="leading-none">{group.emoji}</span>
            <span className="leading-none">{group.count}</span>
          </div>
        </CustomTooltip>
      ))}
    </div>
  );
};
